import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, get, post, put } from '@/shared/api/api-client';
import { showSuccess } from '@/shared/lib/toast-helpers';

/**
 * Lab vendors (PROD-2652) — the labs a clinic sends tests to, plus its own
 * in-house lab. Master data, owned by Scrips.Master (PROD-2651, Master #289).
 *
 * Wire endpoints (`LabVendorsController`, DTOs camelCase):
 *   GET  /api/Master/LabVendors?search&includeInactive&includeGlobal&skip&take → { items, total }
 *   GET  /api/Master/LabVendors/{id}
 *   POST /api/Master/LabVendors                  body: LabVendorInput → 201 LabVendor
 *   PUT  /api/Master/LabVendors/{id}             body: LabVendorInput
 *   POST /api/Master/LabVendors/{id}/deactivate
 *   POST /api/Master/LabVendors/{id}/reactivate
 * There is no delete.
 *
 * Errors worth knowing about:
 *   400 ValidationProblemDetails, keyed by field (`name`, `shortCode`, `email`,
 *       `defaultTurnaroundDays`). The short code is upper-cased server-side and
 *       unique per organization.
 *   403 on writes without the `Permissions` claim `Manage_Lab_Master_Data`
 *       (PROD-2658, Master #290).
 *   503 "not available yet" while `db/lab-vendor/01_create_labvendor.sql` has
 *       not run on the environment. The page renders a friendly state for it,
 *       so the list query opts out of the global error toast.
 */

const MASTER_BASE = '/api/Master';

export const LAB_VENDOR_TYPES = ['InHouse', 'External'] as const;
export const LAB_VENDOR_BILLING = ['InHouse', 'ClientBill', 'PatientBill', 'InsuranceDirect'] as const;
export const LAB_VENDOR_INTEGRATIONS = ['Manual', 'HL7', 'Malaffi', 'FHIR'] as const;

export type LabVendorType = (typeof LAB_VENDOR_TYPES)[number];
export type LabVendorBilling = (typeof LAB_VENDOR_BILLING)[number];
export type LabVendorIntegration = (typeof LAB_VENDOR_INTEGRATIONS)[number];

/** `LabVendorDto` in Scrips.Master `LabVendorDTOs.cs`. */
export interface LabVendor {
  id: string;
  organizationId?: string | null;
  isGlobalTemplate: boolean;
  name: string;
  shortCode: string;
  type: LabVendorType | string;
  contactName?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  website?: string | null;
  accountNumber?: string | null;
  billingResponsibility: LabVendorBilling | string;
  integration: LabVendorIntegration | string;
  codeSystemUri?: string | null;
  defaultTurnaroundDays?: number | null;
  /** PROD-2853: when the courier collects specimens, free text ("Daily 4 pm"). */
  courierPickup?: string | null;
  isActive: boolean;
  createdOn?: string;
  createdBy?: string | null;
  updatedOn?: string | null;
  updatedBy?: string | null;
  /** PROD-2853: the last person to save it, from Identity; on the single-vendor read and on writes, not the list. */
  lastSavedByName?: string | null;
}

/** `LabVendorUpsertRequest`. Organization and audit fields come from the token. */
export interface LabVendorInput {
  name: string;
  shortCode: string;
  type: LabVendorType;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  website: string | null;
  accountNumber: string | null;
  billingResponsibility: LabVendorBilling;
  integration: LabVendorIntegration;
  codeSystemUri: string | null;
  defaultTurnaroundDays: number | null;
  /** PROD-2853: 200 characters at most; null clears it. A non-blank value is a 400 until Master's script 03 has run. */
  courierPickup: string | null;
}

export interface LabVendorListResult {
  items: LabVendor[];
  total: number;
}

export interface UseLabVendorsParams {
  search?: string;
  includeInactive?: boolean;
  includeGlobal?: boolean;
  skip?: number;
  /** 1..200 server-side; default 25 here. */
  take?: number;
}

export const LAB_VENDOR_MAX_TAKE = 200;

export const labVendorKeys = {
  all: ['master', 'lab-vendors'] as const,
  list: (p: UseLabVendorsParams) =>
    [
      ...labVendorKeys.all,
      'list',
      p.search ?? '',
      p.includeInactive ?? false,
      p.includeGlobal ?? false,
      p.skip ?? 0,
      p.take ?? 25,
    ] as const,
  detail: (id: string | undefined) => [...labVendorKeys.all, 'detail', id] as const,
};

export function buildLabVendorListQuery(p: UseLabVendorsParams): string {
  const qs = new URLSearchParams();
  const search = p.search?.trim();
  if (search) qs.set('search', search);
  qs.set('includeInactive', String(p.includeInactive ?? false));
  qs.set('includeGlobal', String(p.includeGlobal ?? false));
  qs.set('skip', String(Math.max(0, p.skip ?? 0)));
  qs.set('take', String(Math.min(LAB_VENDOR_MAX_TAKE, Math.max(1, p.take ?? 25))));
  return qs.toString();
}

/** True for the 503 the API returns until the LabVendor table exists. */
export function isLabVendorsUnavailable(error: unknown): boolean {
  return error instanceof ApiError && error.status === 503;
}

/** Defensive shape mapping: a missing array or total never crashes the table. */
function toListResult(raw: Partial<LabVendorListResult> | null | undefined): LabVendorListResult {
  const items = Array.isArray(raw?.items) ? raw.items : [];
  return { items, total: typeof raw?.total === 'number' ? raw.total : items.length };
}

export function useLabVendors(params: UseLabVendorsParams = {}) {
  return useQuery({
    queryKey: labVendorKeys.list(params),
    queryFn: async ({ signal }) =>
      toListResult(
        await get<LabVendorListResult>(
          `${MASTER_BASE}/LabVendors?${buildLabVendorListQuery(params)}`,
          { signal },
        ),
      ),
    placeholderData: keepPreviousData,
    // A 503 means the table script has not run here; retrying won't help.
    retry: (count, error) => !isLabVendorsUnavailable(error) && count < 1,
    // The page owns the error UI (the 503 state and an inline error card).
    meta: { skipGlobalError: true },
  });
}

export function useLabVendor(id: string | undefined) {
  return useQuery({
    queryKey: labVendorKeys.detail(id),
    queryFn: ({ signal }) => get<LabVendor>(`${MASTER_BASE}/LabVendors/${id}`, { signal }),
    enabled: Boolean(id),
    retry: (count, error) => !isLabVendorsUnavailable(error) && count < 1,
    // The vendor sheet reads it for "Last saved by" only, and falls back to the list row.
    meta: { skipGlobalError: true },
  });
}

export function useCreateLabVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['createLabVendor'],
    mutationFn: (input: LabVendorInput) => post<LabVendor>(`${MASTER_BASE}/LabVendors`, input),
    // The dialog shows 400 field messages inline and toasts anything else itself.
    meta: { skipGlobalError: true },
    onSuccess: () => {
      showSuccess('Lab vendor added');
      void qc.invalidateQueries({ queryKey: labVendorKeys.all });
    },
  });
}

export function useUpdateLabVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['updateLabVendor'],
    mutationFn: ({ id, input }: { id: string; input: LabVendorInput }) =>
      put<LabVendor>(`${MASTER_BASE}/LabVendors/${id}`, input),
    meta: { skipGlobalError: true },
    onSuccess: () => {
      showSuccess('Lab vendor updated');
      void qc.invalidateQueries({ queryKey: labVendorKeys.all });
    },
  });
}

export function useDeactivateLabVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['deactivateLabVendor'],
    mutationFn: (id: string) => post<LabVendor>(`${MASTER_BASE}/LabVendors/${id}/deactivate`, {}),
    onSuccess: () => {
      showSuccess('Lab vendor deactivated');
      void qc.invalidateQueries({ queryKey: labVendorKeys.all });
    },
  });
}

export function useReactivateLabVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['reactivateLabVendor'],
    mutationFn: (id: string) => post<LabVendor>(`${MASTER_BASE}/LabVendors/${id}/reactivate`, {}),
    onSuccess: () => {
      showSuccess('Lab vendor reactivated');
      void qc.invalidateQueries({ queryKey: labVendorKeys.all });
    },
  });
}
